import { ComponentType, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useMountedState from 'react-use/lib/useMountedState';
import Loader, { Props as LoaderProps } from './components/Loader';

interface Props<T, C> {
  resolve?: () => Promise<unknown>;
  onSuccess?: (result: unknown) => void;
  onError?: (error: unknown) => void;
  redirectOnError?: string;
  successComponent: ComponentType<T>;
  successProps?: T; // Renamed from componentProps to match existing usage
  loaderComponent?: ComponentType<C>;
  loaderProps?: C;
}

// Use: <Resolver<SuccessComponentProps, LoaderComponentProps> />
const Resolver = <T extends object, C extends object = LoaderProps>({
  resolve,
  onSuccess,
  onError,
  redirectOnError,
  successComponent: SuccessComponent,
  successProps, // Keep this name to match the fix in FileManagerResolver
  loaderComponent: LoaderComponent = Loader,
  loaderProps = { isLoading: true } as C,
}: Props<T, C>) => {
  const navigate = useNavigate();
  const isMounted = useMountedState();
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    if (resolve) {
      resolve()
        .then(result => {
          if (isCancelled || !isMounted()) return;
          if (onSuccess) onSuccess(result);
          setResolved(true);
        })
        .catch(error => {
          if (isCancelled || !isMounted()) return;
          if (onError) onError(error);
          if (redirectOnError) navigate(redirectOnError);
        });
    } else {
      setResolved(true);
    }

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  if (!resolved) {
    return <LoaderComponent {...(loaderProps as C)} />;
  }

  // Spread successProps as T to satisfy the component's expected props
  return <SuccessComponent {...(successProps as T)} />;
};

export default Resolver;
